<template>
  <v-card id="integration-exchange-list">
    <v-card-text class="pb-0">
      <v-row dense>
        <v-col
          cols="12"
          md="3"
        >
          <v-select
            v-model="filters.sources"
            :items="sourceItems"
            multiple
            chips
            small-chips
            deletable-chips
            dense
            outlined
            hide-details
            clearable
            label="Системы"
          ></v-select>
        </v-col>

        <v-col
          cols="12"
          md="3"
        >
          <v-menu
            v-model="datesOpened"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template #activator="{ on, attrs }">
              <v-text-field
                :value="datesLabel"
                readonly
                dense
                outlined
                hide-details
                clearable
                label="Период"
                :prepend-inner-icon="icons.mdiCalendarRange"
                v-bind="attrs"
                v-on="on"
                @click:clear="clearDates"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="dates"
              range
              locale="ru"
              first-day-of-week="1"
              @change="applyDates"
            ></v-date-picker>
          </v-menu>
        </v-col>

        <v-col
          cols="12"
          md="2"
        >
          <v-text-field
            v-model="filters.orderId"
            dense
            outlined
            hide-details
            clearable
            type="number"
            label="Номер заказа"
          ></v-text-field>
        </v-col>

        <v-col
          cols="12"
          md="2"
        >
          <v-text-field
            v-model="filters.slowerThanMs"
            dense
            outlined
            hide-details
            clearable
            type="number"
            label="Дольше, мс"
          ></v-text-field>
        </v-col>

        <v-col
          cols="12"
          md="2"
          class="d-flex align-center"
        >
          <v-switch
            v-model="filters.onlyFailed"
            dense
            hide-details
            class="mt-0"
            label="Только неудачные"
          ></v-switch>
        </v-col>
      </v-row>
    </v-card-text>

    <v-card-text>
      <div class="d-flex align-center flex-wrap">
        <v-btn
          outlined
          small
          :loading="loading || statsLoading"
          @click="refresh"
        >
          <v-icon
            size="18"
            class="me-2"
          >
            {{ icons.mdiRefresh }}
          </v-icon>
          <span>Обновить</span>
        </v-btn>

        <v-spacer></v-spacer>

        <span class="text--secondary text-caption">Найдено: {{ totalCount }}</span>
      </div>
    </v-card-text>

    <v-card-text v-if="stats.length">
      <span class="d-block text--secondary mb-2">
        Сколько занимают запросы — по выбранным фильтрам, самое долгое сверху
      </span>
      <v-simple-table dense>
        <thead>
          <tr>
            <th class="text-left">
              Система
            </th>
            <th class="text-left">
              Операция
            </th>
            <th class="text-right">
              Запросов
            </th>
            <th class="text-right">
              Неудачных
            </th>
            <th class="text-right">
              Среднее, мс
            </th>
            <th class="text-right">
              Максимум, мс
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in stats"
            :key="`${row.source}-${row.operation}`"
          >
            <td>{{ row.source }}</td>
            <td>{{ row.operation || '—' }}</td>
            <td class="text-right">
              {{ row.count }}
            </td>
            <td
              class="text-right"
              :class="row.failed ? 'error--text font-weight-medium' : 'text--secondary'"
            >
              {{ row.failed }}
            </td>
            <td
              class="text-right"
              :class="slow(row.averageMs) ? 'error--text font-weight-medium' : ''"
            >
              {{ row.averageMs }}
            </td>
            <td
              class="text-right"
              :class="slow(row.maxMs) ? 'error--text font-weight-medium' : ''"
            >
              {{ row.maxMs }}
            </td>
          </tr>
        </tbody>
      </v-simple-table>
    </v-card-text>

    <v-data-table
      :headers="headers"
      :items="exchanges"
      :server-items-length="totalCount"
      :loading="loading"
      :page.sync="filters.pageNumber"
      :items-per-page.sync="filters.pageSize"
      :sort-by.sync="filters.sortBy"
      :sort-desc.sync="filters.sortDesc"
      :footer-props="{ 'items-per-page-options': [25, 50, 100] }"
      class="text-no-wrap"
      @click:row="open"
    >
      <template #[`item.createdAt`]="{item}">
        {{ formatDate(item.createdAt) }}
      </template>

      <template #[`item.statusCode`]="{item}">
        <v-chip
          small
          :color="item.statusCode ? (item.isSuccess ? 'success' : 'error') : 'error'"
          :class="`${item.statusCode && item.isSuccess ? 'success' : 'error'}--text`"
        >
          {{ item.statusCode || 'нет ответа' }}
        </v-chip>
      </template>

      <template #[`item.durationMs`]="{item}">
        <span :class="slow(item.durationMs) ? 'error--text font-weight-medium' : ''">
          {{ item.durationMs }}
        </span>
      </template>

      <template #[`item.orderId`]="{item}">
        <router-link
          v-if="item.orderId"
          :to="{ name: 'order-form-tab', params: { id: item.orderId } }"
        >
          {{ item.deliveryOrderId || item.orderId }}
        </router-link>
        <v-tooltip
          v-else
          bottom
        >
          <template #activator="{ on, attrs }">
            <span
              class="text--secondary"
              v-bind="attrs"
              v-on="on"
            >—</span>
          </template>
          <span>Запись сделана до того, как заказ появился: расчёт корзины или справочник</span>
        </v-tooltip>
      </template>

      <template #[`item.error`]="{item}">
        <span
          v-if="item.error"
          class="error--text"
        >{{ item.error }}</span>
        <span v-else>—</span>
      </template>
    </v-data-table>

    <v-dialog
      :value="!!details"
      max-width="1100"
      scrollable
      @input="close"
    >
      <v-card v-if="details">
        <v-card-title class="d-flex align-center flex-wrap">
          <span class="me-3">{{ details.source }}</span>
          <span class="text--secondary me-3">{{ details.operation }}</span>
          <span
            class="text-body-2 me-3"
            :class="slow(details.durationMs) ? 'error--text font-weight-medium' : 'text--secondary'"
          >
            {{ details.durationMs }} мс
          </span>
          <span class="text-body-2 text--secondary">{{ formatDate(details.createdAt) }}</span>
        </v-card-title>

        <v-card-text>
          <v-alert
            v-if="details.error"
            text
            color="error"
            dense
          >
            Запрос не состоялся: {{ details.error }}
          </v-alert>

          <div class="mb-4">
            <span class="d-block text--secondary">Адрес</span>
            <code class="exchange-url">{{ details.method }} {{ details.url }}</code>
          </div>

          <v-row>
            <v-col cols="12">
              <span class="d-block text--secondary mb-1">Заголовки запроса</span>
              <pre class="exchange-block">{{ details.requestHeaders || '—' }}</pre>
            </v-col>

            <v-col
              cols="12"
              md="6"
            >
              <div class="d-flex align-center mb-1">
                <span class="text--secondary">Тело запроса</span>
                <v-spacer></v-spacer>
                <v-btn
                  x-small
                  text
                  @click="copy(details.requestBody)"
                >
                  Скопировать
                </v-btn>
              </div>
              <pre class="exchange-block">{{ details.requestBody || '—' }}</pre>
            </v-col>

            <v-col
              cols="12"
              md="6"
            >
              <div class="d-flex align-center mb-1">
                <span class="text--secondary">Тело ответа</span>
                <v-spacer></v-spacer>
                <v-btn
                  x-small
                  text
                  @click="copy(details.responseBody)"
                >
                  Скопировать
                </v-btn>
              </div>
              <pre class="exchange-block">{{ details.responseBody || '—' }}</pre>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="close"
          >
            Закрыть
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { mdiCalendarRange, mdiRefresh } from '@mdi/js'
import { mapFormHandlers } from '@/helpers/forms'

const SLOW_MS = 3000

export default {
  name: 'IntegrationExchangeList',
  data: () => ({
    icons: { mdiCalendarRange, mdiRefresh },
    datesOpened: false,
    dates: [],
    sourceItems: [
      { text: 'Касса', value: 'Rk7' },
      { text: 'CRM', value: 'Crm' },
      { text: 'СМС', value: 'Nikita' },
    ],
  }),
  computed: {
    ...mapFormHandlers('integrationExchange', [
      'filters.pageNumber',
      'filters.pageSize',
      'filters.sortBy',
      'filters.sortDesc',
      'filters.sources',
      'filters.dateFrom',
      'filters.dateTo',
      'filters.orderId',
      'filters.onlyFailed',
      'filters.slowerThanMs',
    ]),
    ...mapState('integrationExchange', {
      exchanges: 'list',
      totalCount: 'totalCount',
      loading: 'loading',
      stats: 'stats',
      statsLoading: 'statsLoading',
      details: 'details',
    }),
    datesLabel() {
      const { dateFrom, dateTo } = this.filters

      if (!dateFrom) return ''

      return dateFrom === dateTo ? dateFrom : `${dateFrom} — ${dateTo}`
    },
    headers() {
      return [
        { text: 'Время', value: 'createdAt' },
        { text: 'Система', value: 'source', sortable: false },
        { text: 'Операция', value: 'operation', sortable: false },
        { text: 'Заказ', value: 'orderId', sortable: false },
        { text: 'Ответ', value: 'statusCode', align: 'center', sortable: false },
        { text: 'мс', value: 'durationMs', align: 'right' },
        { text: 'Ошибка', value: 'error', sortable: false },
      ]
    },
  },
  async created() {
    document.title = 'Обмен с внешними системами'
    await this.refresh()
  },
  methods: {
    ...mapActions('integrationExchange', ['fetchList', 'fetchStats', 'fetchDetails', 'closeDetails']),
    async refresh() {
      await this.fetchList()
      await this.fetchStats()
    },
    slow(ms) {
      return ms >= SLOW_MS
    },
    formatDate(value) {
      if (!value) return ''

      return new Date(value).toLocaleString('ru-RU')
    },

    applyDates(range) {
      const [first, second] = [...range].sort()

      this.filters.dateFrom = first
      this.filters.dateTo = second || first

      if (range.length === 2) this.datesOpened = false
    },
    clearDates() {
      this.dates = []
      this.filters.dateFrom = null
      this.filters.dateTo = null
    },
    async open(row) {
      await this.fetchDetails(row.id)
    },
    close() {
      this.closeDetails()
    },
    copy(body) {
      if (!body || !navigator.clipboard) return

      navigator.clipboard.writeText(body)
    },
  },
}
</script>

<style lang="scss" scoped>
#integration-exchange-list {
  ::v-deep tbody tr {
    cursor: pointer;
  }
}

.exchange-block {
  max-height: 320px;
  overflow: auto;
  padding: 8px;
  border-radius: 4px;
  background: rgba(127, 127, 127, 0.08);
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-word;
}

.exchange-url {
  word-break: break-all;
}
</style>
